import { Component } from '@angular/core';
import { GenericSliderPage } from '@uigy/shared/components/slider-v2/models/generic-slider-page';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { LanguageService } from '@uigy/shared/services/language.service';
import { FormValidatorsService } from '@uigy/shared/services/form-validators.service';
import { ValidateIp } from '@uigy/shared/form-validators/ip.validator';
import Utils from '@uigy/utils';
import { EditSensorSliderData } from '@uigy/modules/sensors/interfaces/interfaces';
import { SensorStatus } from '@uigy/generated/graphql';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';

@Component({
    selector: 'app-edit-sensor',
    templateUrl: './edit-sensor.component.html',
    styleUrls: ['./edit-sensor.component.scss'],
    standalone: false
})
export class EditSensorComponent implements GenericSliderPage<EditSensorSliderData> {
  public readonly editSensorTexts = this.languageService.texts.settings.system_config.sensors.edit_sensor;

  public toasterType = ToasterType;

  private ogSensor!: EditSensorSliderData;

  public showActiveNetworkElements: boolean = true;
  public hasDuplicateNetwork: boolean = false;

  public form: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    activeNetworks: new UntypedFormControl('', [ValidateIp]),
    active: new UntypedFormControl(false)
  });

  constructor(private languageService: LanguageService,
              public formValidatorsService: FormValidatorsService) {
  }

  public getData(): EditSensorSliderData {
    return {
      name: this.form.value.name,
      activeNetworks: this.ogSensor.duplicateNetworks ? this.ogSensor.activeNetworks :
        this.showActiveNetworkElements ? Utils.multiLineTextToArray(this.form.value?.activeNetworks) : [],
      active: this.form.value.active ?? false,
      id: this.ogSensor.id,
      status: this.ogSensor.status
    };
  }

  public initData(data: EditSensorSliderData) {
    this.ogSensor = data;

    // type casting because of graphql-codegen bug
    let activeNetworks = data.activeNetworks as string[];
    if (data.duplicateNetworks && data.duplicateNetworks.length > 0) {
      this.activeNetworks?.disable();
      this.hasDuplicateNetwork = true;
    }
    // if unauthenticated then disable remoteNetworks and active fields
    else if (data.status === SensorStatus.ConnectedUnauthenticated) {
      this.activeNetworks?.disable();
      this.active?.disable();
      this.showActiveNetworkElements = false;
    } else {
      this.activeNetworks?.valueChanges.subscribe(() => this.activeNetworkHandleChange());

      if (this.activeNetworks?.value?.length === 0 && !this.hasDuplicateNetwork) {
        this.active?.disable();
      }
    }

    // set values
    this.form.patchValue({
      activeNetworks: Utils.arrayToMultiLineText(activeNetworks),
      active: data.active,
      name: data.name
    });
  }

  private activeNetworkHandleChange(): void {
    if (!this.hasDuplicateNetwork) {
      if (!this.activeNetworks?.valid) {
        this.active?.disable();
        return;
      }

      if (this.activeNetworks?.value?.length === 0) {
        // if activeNetworks empty set active to false and disable
        this.form.patchValue({
          active: false
        });
        this.active?.disable();
        return;
      }

      this.active?.enable();
    }
  }

  public isValid(): boolean {
    return this.form.valid;
  }

  public get activeNetworks(): AbstractControl | null {
    return this.form.get('activeNetworks');
  }

  private get active(): AbstractControl | null {
    return this.form.get('active');
  }

  private get name(): AbstractControl | null {
    return this.form.get('name');
  }
}
